<script setup lang="ts">
import type { HomeType } from '~/composables/useHomeType'

/**
 * PopularDestinationsV3 — maillage accueil → pages département.
 *
 * Affiche les 12 départements les plus fournis POUR LE TYPE ACTIF de la home
 * (Tout = chambres d'hôtes + gîtes, CDH, Gîtes), en liens `<a href>` rendus en
 * SSR vers `/${code}-${slug}`. La page département affiche les chambres d'hôtes
 * par défaut : le lien porte donc le type actif pour que la page montre ce que
 * le compteur annonce — `?type=all` en Tout (CDH + gîtes), `?type=gite` en Gîtes
 * (seuls les départements avec gîtes apparaissent). En CDH le lien reste nu :
 * URL canonique du département.
 */
export interface PopularDepartment {
  code: string
  name: string
  slug: string
  establishmentCount: number
  giteCount?: number
}

const props = withDefaults(defineProps<{
  departments: PopularDepartment[]
  type?: HomeType
  limit?: number
}>(), {
  type: 'all',
  limit: 12,
})

const { t } = useI18n()
const localePath = useLocalePath()

function countFor(dept: PopularDepartment): number {
  const gites = dept.giteCount ?? 0
  if (props.type === 'gite') return gites
  if (props.type === 'cdh') return dept.establishmentCount
  return dept.establishmentCount + gites
}

const items = computed(() =>
  props.departments
    .map(dept => ({ dept, count: countFor(dept) }))
    .filter(item => item.count > 0)
    // Tri stable : densité décroissante, puis code pour un ordre déterministe
    // (pas de mismatch d'hydratation entre serveur et client).
    .sort((a, b) => b.count - a.count || a.dept.code.localeCompare(b.dept.code))
    .slice(0, props.limit)
    .map(({ dept, count }) => {
      return {
        key: dept.code,
        name: dept.name,
        code: dept.code,
        countLabel: t('pages.home.popularDestinations.count', { count }, count),
        to: props.type === 'cdh'
          ? localePath(`/${dept.code}-${dept.slug}`)
          : { path: localePath(`/${dept.code}-${dept.slug}`), query: { type: props.type } },
      }
    }),
)
</script>

<template>
  <section
    v-if="items.length > 0"
    id="popular-destinations"
    class="py-20 md:py-28 bg-paper-50 text-paper-900"
    aria-labelledby="popular-destinations-title"
  >
    <div class="container mx-auto px-4 lg:px-8">
      <header class="max-w-2xl mb-10 md:mb-14">
        <p class="tag text-sage-700 mb-4">
          {{ $t('pages.home.popularDestinations.eyebrow') }}
        </p>
        <h2
          id="popular-destinations-title"
          class="font-display font-light tracking-[-0.02em] leading-tight text-3xl md:text-5xl text-paper-900 mb-4"
        >
          {{ $t('pages.home.popularDestinations.title') }}
        </h2>
        <p class="font-sans text-base md:text-lg text-paper-900/70 leading-relaxed">
          {{ $t(`pages.home.popularDestinations.subtitle.${type}`) }}
        </p>
      </header>

      <ul class="max-w-[1120px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 sm:gap-x-10 border-t hairline">
        <li
          v-for="item in items"
          :key="item.key"
          class="group border-b hairline"
        >
          <NuxtLink
            :to="item.to"
            class="flex items-baseline gap-3 py-4 px-2 hover:bg-paper-100/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700 transition-colors"
          >
            <span class="font-mono text-[11px] text-paper-900/50 w-8 shrink-0 tabular-nums">
              {{ item.code }}
            </span>
            <span class="flex-1 min-w-0 font-display text-xl leading-tight group-hover:italic">
              {{ item.name }}
            </span>
            <span class="font-mono text-[12px] text-paper-900/60 tabular-nums shrink-0">
              {{ item.countLabel }}
            </span>
          </NuxtLink>
        </li>
      </ul>

      <div class="max-w-[1120px] mt-8">
        <NuxtLink
          :to="localePath('/departments')"
          class="inline-flex items-center gap-2 font-sans text-sm font-medium text-sage-700 underline underline-offset-4 hover:text-sage-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700"
        >
          {{ $t('pages.home.popularDestinations.allCta') }}
          <UIcon name="i-lucide-arrow-right" class="size-4" aria-hidden="true" />
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
